import { useState, useMemo } from "react";
import api, { extractApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Check, X, Bookmark, BookmarkCheck, ChevronDown, RotateCcw, Info,
  BookOpen,
} from "lucide-react";
import { SkillGapPanel } from "@/components/m3/SkillGapPanel";
import { formatQidRefs, QidChipList } from "@/lib/qidFormat";

// M-W7 Stage 2 (2E): plain-language chips.
const CATEGORY_LABEL = {
  closest_transition:      "Builds on your experience",
  ai_supported_growth:     "Grows with AI skills",
  independent_opportunity: "Independent path",
  other:                   "Honest alternative",
};

const CATEGORY_COLOR = {
  closest_transition: "border-progress-teal/40 text-progress-teal bg-progress-teal/10",
  ai_supported_growth: "border-accent-warm/40 text-accent-warm bg-accent-warm/10",
  independent_opportunity: "border-fg-secondary/40 text-fg-secondary bg-bg-surface-hover",
  other: "border-border-subtle text-fg-muted bg-bg-base",
};

// 2026-09-23c Three Directions clarity: per-question-section evidence
// labels so a supporting_answers list surfaces plain-language provenance
// (stated interest vs demonstrated experience vs possible strength). No
// invented labels — the mapping is deterministic based on the assessment
// section a question belongs to.
const SECTION_EVIDENCE_LABEL = {
  s1: "Real-life context",
  s2: "Demonstrated experience",
  s3: "Demonstrated strength",       // overridden to "Possible strength" if the
                                     // participant selected the strength
                                     // without giving an example.
  s4: "Stated interest",
  s5: "Learning preference",
  s6: "Digital comfort baseline",
};

function evidenceLabelFor(qid, answer) {
  const m = /^s(\d)_/.exec(String(qid || ""));
  if (!m) return null;
  const sec = `s${m[1]}`;
  if (sec === "s3") {
    // strength-with-example vs strength-only. `answer` is an array of
    // {strength, example} — if ANY selected strength has no example
    // text we soften the label to "Possible strength".
    if (Array.isArray(answer) && answer.some((s) => !s?.example?.trim())) {
      return "Possible strength";
    }
    return "Demonstrated strength";
  }
  return SECTION_EVIDENCE_LABEL[sec] || null;
}

// Numbered direction badge — mixes number + icon shape so we never rely
// on color alone (Mark's accessibility requirement). Icons come from
// lucide-react; each direction gets a distinct SHAPE not a distinct hue.
import {
  Circle as _Circle, Square as _Square, Triangle as _Triangle,
} from "lucide-react";
const NUMBER_SHAPES = [_Circle, _Square, _Triangle];

/**
 * Compute per-direction status flags from the `actions` audit log.
 * Latest action for (direction_id, kind) wins. Symmetric pairs:
 *   select   / unselect
 *   reject   / unreject
 *   save     / unsave
 */
function computeStatuses(actions, directionId) {
  const rel = (actions || [])
    .filter((a) => a.direction_id === directionId)
    .slice() // don't mutate
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const status = { selected: false, saved: false, rejected: false, reject_reason: null };
  const seenKinds = new Set();
  for (const a of rel) {
    if (a.action === "select" && !seenKinds.has("select_pair")) {
      status.selected = true; seenKinds.add("select_pair");
    }
    if (a.action === "unselect" && !seenKinds.has("select_pair")) {
      status.selected = false; seenKinds.add("select_pair");
    }
    if (a.action === "reject" && !seenKinds.has("reject_pair")) {
      status.rejected = true; status.reject_reason = a.reason || null; seenKinds.add("reject_pair");
    }
    if (a.action === "unreject" && !seenKinds.has("reject_pair")) {
      status.rejected = false; seenKinds.add("reject_pair");
    }
    if (a.action === "save" && !seenKinds.has("save_pair")) {
      status.saved = true; seenKinds.add("save_pair");
    }
    if (a.action === "unsave" && !seenKinds.has("save_pair")) {
      status.saved = false; seenKinds.add("save_pair");
    }
  }
  return status;
}

export function DirectionCard({
  direction, setId, isCurrentSet, actions, answersById, questionMap,
  selectedOtherId, onUpdate, positionNumber,
}) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const st = useMemo(() => computeStatuses(actions, direction.id), [actions, direction.id]);
  const anotherSelected = selectedOtherId && selectedOtherId !== direction.id;

  const send = async (action, reason = null) => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/directions/action", {
        set_id: setId, direction_id: direction.id, action, reason,
      });
      onUpdate?.(data);
      const msg = {
        select: "You marked this direction to explore. Your answers stay saved — you can switch or clear this anytime.",
        unselect: "Selection cleared. Your answers stay saved.",
        save: "Saved to your list.",
        unsave: "Removed from saved.",
        reject: "Direction rejected. It stays in your history.",
        unreject: "Direction restored.",
      }[action];
      if (msg) toast.success(msg);
    } catch (err) {
      setError(extractApiError(err, "Action failed."));
    } finally {
      setBusy(false);
    }
  };

  const onReject = async () => {
    await send("reject", rejectReason.trim() || null);
    setRejectOpen(false);
    setRejectReason("");
  };

  const catClass = CATEGORY_COLOR[direction.category] || CATEGORY_COLOR.other;
  // Number + shape (not color) badge for 3-way visual distinction.
  const posIdx = Number.isInteger(positionNumber) ? positionNumber - 1 : null;
  const ShapeIcon = posIdx != null && posIdx >= 0 && posIdx < NUMBER_SHAPES.length
    ? NUMBER_SHAPES[posIdx] : null;

  return (
    <div
      data-testid={`direction-${direction.id}`}
      data-category={direction.category}
      data-selected={st.selected}
      data-rejected={st.rejected}
      className={
        "rounded-lg border p-6 flex flex-col " +
        (st.rejected
          ? "border-destructive/40 bg-bg-surface opacity-60"
          : st.selected
          ? "border-accent-warm bg-bg-surface"
          : "border-border-subtle bg-bg-surface")
      }
    >
      {/* header */}
      <div className="flex flex-wrap items-start gap-2">
        {ShapeIcon && (
          <span
            data-testid={`direction-${direction.id}-number-badge`}
            data-position={positionNumber}
            className="inline-flex items-center gap-1.5 rounded-md border border-fg-primary/30 bg-bg-base px-2 py-1 text-[11px] font-medium uppercase tracking-wider text-fg-primary"
            aria-label={`Direction ${positionNumber} of 3`}
          >
            <ShapeIcon className="h-3.5 w-3.5" aria-hidden="true" />
            Direction {positionNumber}
          </span>
        )}
        <span data-testid={`direction-${direction.id}-chip`} className={"text-[11px] uppercase tracking-wider px-2 py-1 rounded border " + catClass}>
          {CATEGORY_LABEL[direction.category] || direction.category}
        </span>
        {st.selected && (
          <span data-testid={`direction-${direction.id}-selected-badge`} className="text-[11px] uppercase tracking-wider px-2 py-1 rounded border border-accent-warm text-accent-warm bg-accent-warm/10">
            <Check className="h-3 w-3 inline mr-1" aria-hidden="true" />
            You're exploring this one
          </span>
        )}
        {st.saved && !st.selected && (
          <span data-testid={`direction-${direction.id}-saved-badge`} className="text-[11px] uppercase tracking-wider px-2 py-1 rounded border border-progress-teal/40 text-progress-teal bg-progress-teal/10">
            <BookmarkCheck className="h-3 w-3 inline mr-1" aria-hidden="true" />
            Saved
          </span>
        )}
        {st.rejected && (
          <span data-testid={`direction-${direction.id}-rejected-badge`} className="text-[11px] uppercase tracking-wider px-2 py-1 rounded border border-destructive/40 text-destructive bg-destructive/10">
            Rejected
          </span>
        )}
      </div>

      <h3 className="mt-3 text-xl font-heading font-medium text-fg-primary" data-testid={`direction-${direction.id}-title`}>
        {formatQidRefs(direction.title)}
      </h3>

      <p className="mt-2 text-sm leading-relaxed text-fg-secondary" data-testid={`direction-${direction.id}-description`}>
        {formatQidRefs(direction.description)}
      </p>

      {/* Why we suggest this — grounded, supportive framing (Mark 2026-09-23c). */}
      <div
        data-testid={`direction-${direction.id}-why-we-suggest`}
        className="mt-4 rounded-md border border-progress-teal/30 bg-progress-teal/5 p-3"
      >
        <p className="text-[11px] uppercase tracking-wider text-progress-teal">
          Why we suggest this
        </p>
        <p className="mt-1 text-sm text-fg-primary">
          <span className="italic text-fg-secondary">This may be worth exploring because…</span>{" "}
          {formatQidRefs(direction.why_it_may_fit) || "—"}
        </p>
      </div>

      {direction.category_limitation && (
        <div data-testid={`direction-${direction.id}-limitation`} className="mt-3 rounded-md border border-fg-muted/40 bg-bg-base p-3">
          <p className="text-[11px] uppercase tracking-wider text-fg-secondary flex items-center gap-1">
            <Info className="h-3 w-3" aria-hidden="true" />
            Category disclosure
          </p>
          <p data-testid={`direction-${direction.id}-limitation-text`} className="mt-1 text-xs text-fg-secondary italic">
            {formatQidRefs(direction.category_limitation)}
          </p>
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <FieldBlock testid={`direction-${direction.id}-tasks`} label="Typical tasks & environment" body={direction.typical_tasks_and_environment} />
        <FieldBlock testid={`direction-${direction.id}-effort`} label="Learning effort" body={direction.learning_effort_note} />
        <ListBlock testid={`direction-${direction.id}-transferable`} label="Skills you already have" items={direction.transferable_skills} />
        <ListBlock testid={`direction-${direction.id}-develop`} label="Skills to develop" items={direction.skills_to_develop} />
        <FieldBlock testid={`direction-${direction.id}-limits`} label="Limitations & tradeoffs" body={direction.limitations_and_tradeoffs} />
        <FieldBlock testid={`direction-${direction.id}-why`} label="Why this may fit (full)" body={direction.why_it_may_fit} />
      </div>

      <div className="mt-4 rounded-md border border-progress-teal/30 bg-progress-teal/10 p-3">
        <p className="text-xs uppercase tracking-wider text-progress-teal">Practical first step this week</p>
        <p data-testid={`direction-${direction.id}-first-step`} className="mt-1 text-sm text-fg-primary">
          {formatQidRefs(direction.practical_first_step)}
        </p>
      </div>

      <div className="mt-3 rounded-md border border-border-subtle bg-bg-base p-3">
        <p className="text-xs uppercase tracking-wider text-fg-secondary flex items-center gap-1">
          <Info className="h-3 w-3" aria-hidden="true" />
          Labor market
        </p>
        <p data-testid={`direction-${direction.id}-labor`} className="mt-1 text-xs text-fg-secondary italic">
          {direction.labor_market_status}
        </p>
      </div>

      {direction.distinctness_rationale && (
        <p data-testid={`direction-${direction.id}-distinct`} className="mt-3 text-xs text-fg-muted">
          How this differs: {formatQidRefs(direction.distinctness_rationale)}
        </p>
      )}

      {/* evidence expander */}
      <Collapsible className="mt-4">
        <CollapsibleTrigger asChild>
          <button
            data-testid={`direction-${direction.id}-expand-evidence`}
            className="flex items-center gap-2 text-xs text-fg-secondary hover:text-fg-primary transition-colors"
          >
            <ChevronDown className="h-3 w-3" aria-hidden="true" />
            Based on your answers ({direction.supporting_answers?.length || 0})
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2">
          <ul className="space-y-2 pl-2 border-l border-border-subtle">
            {(direction.supporting_answers || []).map((qid) => {
              const q = questionMap?.[qid];
              const a = answersById?.[qid];
              const f = /^s(\d{1,3})_q(\d{1,3})$/i.exec(String(qid).trim());
              const secN = f ? parseInt(f[1], 10) : null;
              const qN = f ? parseInt(f[2], 10) : null;
              const evLabel = evidenceLabelFor(qid, a?.answer);
              return (
                <li key={qid} data-testid={`direction-${direction.id}-evidence-${qid}`} className="text-xs text-fg-secondary pl-3">
                  <div className="flex flex-wrap items-baseline gap-2">
                    {f && (
                      <span
                        data-testid={`direction-${direction.id}-evidence-${qid}-ref`}
                        data-qid={qid}
                        aria-label={`Section ${secN}, Question ${qN}`}
                        className="inline-block rounded border border-border-subtle bg-bg-base px-1.5 py-0.5 text-[11px] font-mono uppercase tracking-wide text-fg-primary whitespace-nowrap"
                      >
                        {`S${secN}_Q${qN}`}
                        <span className="sr-only"> (Section {secN}, Question {qN})</span>
                      </span>
                    )}
                    {evLabel && (
                      <span
                        data-testid={`direction-${direction.id}-evidence-${qid}-type`}
                        data-evidence-type={evLabel}
                        className="inline-block rounded-full border border-progress-teal/40 bg-progress-teal/10 text-progress-teal px-2 py-0.5 text-[10px] uppercase tracking-wider whitespace-nowrap"
                      >
                        {evLabel}
                      </span>
                    )}
                    <p className="text-fg-primary">{q?.prompt || qid}</p>
                  </div>
                  <p className="mt-1 italic">{renderAnswerShort(q, a?.answer, a?.skipped)}</p>
                </li>
              );
            })}
          </ul>
        </CollapsibleContent>
      </Collapsible>

      {/* Skill-gap panel drawer (M-W7 Stage 2 2B) */}
      {direction.skill_gap && (
        <Collapsible className="mt-4">
          <CollapsibleTrigger asChild>
            <button
              data-testid={`direction-${direction.id}-expand-skillgap`}
              className="flex items-center gap-2 text-xs text-fg-secondary hover:text-fg-primary transition-colors"
            >
              <BookOpen className="h-3 w-3" aria-hidden="true" />
              Compare skill gaps for this direction
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-3">
            <SkillGapPanel
              directionId={direction.id}
              answersById={answersById}
              questionMap={questionMap}
            />
          </CollapsibleContent>
        </Collapsible>
      )}

      {st.reject_reason && (
        <p className="mt-3 text-xs text-fg-muted italic">Your reason: {st.reject_reason}</p>
      )}

      {error && <Alert variant="destructive" className="mt-3"><AlertDescription>{error}</AlertDescription></Alert>}

      {/* Actions — only for the CURRENT set */}
      {isCurrentSet && (
        <div className="mt-5 pt-4 border-t border-border-subtle flex flex-wrap gap-2">
          {!st.rejected && !st.selected && (
            <Button
              size="sm"
              onClick={() => send("select")}
              disabled={busy || anotherSelected}
              data-testid={`direction-${direction.id}-select-btn`}
              className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover disabled:opacity-40"
              title={anotherSelected ? "Clear your current choice first — your answers stay saved" : ""}
            >
              <Check className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
              Explore this direction
            </Button>
          )}
          {st.selected && (
            <Button
              size="sm" variant="outline"
              onClick={() => send("unselect")} disabled={busy}
              data-testid={`direction-${direction.id}-unselect-btn`}
              className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
              Change my mind
            </Button>
          )}
          {!st.rejected && (
            <Button
              size="sm" variant="ghost"
              onClick={() => send(st.saved ? "unsave" : "save")} disabled={busy}
              data-testid={`direction-${direction.id}-save-btn`}
              className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
            >
              {st.saved ? (<><BookmarkCheck className="h-3.5 w-3.5 mr-2" aria-hidden="true" />Unsave</>)
                        : (<><Bookmark className="h-3.5 w-3.5 mr-2" aria-hidden="true" />Save for later</>)}
            </Button>
          )}
          {!st.rejected ? (
            !rejectOpen ? (
              <Button
                size="sm" variant="ghost"
                onClick={() => setRejectOpen(true)} disabled={busy}
                data-testid={`direction-${direction.id}-reject-btn`}
                className="text-fg-secondary hover:text-destructive hover:bg-destructive/10"
              >
                <X className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                Not for me
              </Button>
            ) : (
              <div className="w-full flex flex-col gap-2">
                <Textarea
                  data-testid={`direction-${direction.id}-reject-reason`}
                  placeholder="Optional: why doesn't this fit? (max 400 chars)"
                  maxLength={400}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="bg-bg-base border-border-subtle text-fg-primary text-sm"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm" variant="destructive"
                    onClick={onReject} disabled={busy}
                    data-testid={`direction-${direction.id}-reject-confirm`}
                  >Confirm reject</Button>
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => { setRejectOpen(false); setRejectReason(""); }}
                    data-testid={`direction-${direction.id}-reject-cancel`}
                    className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
                  >Cancel</Button>
                </div>
              </div>
            )
          ) : (
            <Button
              size="sm" variant="outline"
              onClick={() => send("unreject")} disabled={busy}
              data-testid={`direction-${direction.id}-restore-btn`}
              className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
              Restore
            </Button>
          )}
        </div>
      )}
      {!isCurrentSet && (
        <p className="mt-4 text-xs text-fg-muted">From an earlier direction set — actions are on the current set only.</p>
      )}
    </div>
  );
}

function FieldBlock({ testid, label, body }) {
  return (
    <div className="rounded-md border border-border-subtle bg-bg-base p-3">
      <p className="text-[11px] uppercase tracking-wider text-fg-secondary">{label}</p>
      <p data-testid={testid} className="mt-1 text-sm text-fg-primary">{formatQidRefs(body)}</p>
    </div>
  );
}

function ListBlock({ testid, label, items }) {
  return (
    <div className="rounded-md border border-border-subtle bg-bg-base p-3">
      <p className="text-[11px] uppercase tracking-wider text-fg-secondary">{label}</p>
      <ul data-testid={testid} className="mt-1 text-sm text-fg-primary list-disc pl-5 space-y-0.5">
        {(items || []).map((s, i) => <li key={i}>{formatQidRefs(s)}</li>)}
      </ul>
    </div>
  );
}

function renderAnswerShort(question, answer, skipped) {
  if (skipped) return "Marked skipped.";
  if (answer === null || answer === undefined || answer === "") return "Not answered.";
  if (!question) return String(answer);
  if (question.type === "text") {
    const s = String(answer);
    return s.length > 240 ? s.slice(0, 240) + "…" : s;
  }
  if (question.type === "single") {
    const o = (question.options || []).find((x) => x.value === answer);
    return o?.label || String(answer);
  }
  if (question.type === "multi") {
    if (!Array.isArray(answer)) return String(answer);
    return answer.map((v) => (question.options || []).find((o) => o.value === v)?.label || v).join(" · ");
  }
  if (question.type === "scale") return `${answer} of ${question.scale_max}`;
  if (question.type === "strengths_with_examples") {
    if (!Array.isArray(answer)) return String(answer);
    return answer.map((s) => s.example ? `${s.strength.replace(/_/g, " ")} — “${s.example}”` : `${s.strength.replace(/_/g, " ")} (no example)`).join(" · ");
  }
  return JSON.stringify(answer);
}

export default DirectionCard;
